/**
 * ApprovalWorkflow-Dot — turn a workflow blob into a graphviz DOT graph.
 *
 * This is the input to the graphviz-WASM renderer: graphviz does the whole diagram (layout, spline edge
 * routing with proper attachment + arrowheads, non-overlapping labels, crossing minimisation) — the thing
 * we were hand-reconstructing in pict-section-flow. Each node/edge carries a stable `id` (s0…, e0…) so the
 * renderer can map an SVG click back to the model via the returned NodeMap / EdgeMap.
 *
 * Pure (no graphviz / DOM here) so it's unit-testable on its own.
 */

const libModel = require('./ApprovalWorkflow-Model.js');

/** Escape a string for a DOT double-quoted label. */
function _escape(pValue)
{
	return String(pValue == null ? '' : pValue).replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, '\\n');
}

/** Wrap text to ~pMax chars per line at word boundaries, joined with real newlines (escaped later). */
function _wrap(pText, pMax)
{
	const tmpWords = String(pText || '').split(/\s+/).filter(Boolean);
	const tmpLines = [];
	let tmpCurrent = '';
	tmpWords.forEach((pWord) =>
	{
		if (tmpCurrent && (tmpCurrent.length + 1 + pWord.length) > pMax)
		{
			tmpLines.push(tmpCurrent);
			tmpCurrent = pWord;
		}
		else
		{
			tmpCurrent = tmpCurrent ? `${tmpCurrent} ${pWord}` : pWord;
		}
	});
	if (tmpCurrent) { tmpLines.push(tmpCurrent); }
	return tmpLines.join('\n');
}

/** Recursively flatten a transition's branches into { ToStatus } entries (incl. nested). */
function _flattenBranchTargets(pBranches)
{
	const tmpOut = [];
	(pBranches || []).forEach((pBranch) =>
	{
		tmpOut.push({ ToStatus: pBranch.ToStatus });
		if (Array.isArray(pBranch.Branches) && pBranch.Branches.length)
		{
			tmpOut.push(..._flattenBranchTargets(pBranch.Branches));
		}
	});
	return tmpOut;
}

/**
 * Build a DOT string + the id→model maps.
 * @param {Object} pWorkflow  the normalized working blob
 * @param {Object} [pOptions]
 * @return {{ Dot: String, NodeMap: Object, EdgeMap: Object }}
 *   NodeMap: { '<nodeId>': '<Status>' }
 *   EdgeMap: { '<edgeId>': { SourceStatus, TransitionIndex, IsBranch, ToStatus } }
 */
function blobToDot(pWorkflow, pOptions)
{
	const tmpStates = (pWorkflow && Array.isArray(pWorkflow.States)) ? pWorkflow.States : [];
	const tmpIdByStatus = {};
	const tmpNodeMap = {};
	tmpStates.forEach((pState, pIndex) =>
	{
		tmpIdByStatus[pState.Status] = `s${pIndex}`;
		tmpNodeMap[`s${pIndex}`] = pState.Status;
	});

	let tmpDot = 'digraph approval {\n';
	tmpDot += '  rankdir=TB;\n';
	tmpDot += '  bgcolor="transparent";\n';
	tmpDot += '  nodesep=0.45;\n';
	tmpDot += '  ranksep=0.55;\n';
	tmpDot += '  splines=spline;\n';
	// fontname is a single PostScript face ("Helvetica") that graphviz has built-in width metrics for — a
	// comma font-stack makes it fall back to Times metrics and mis-size the label boxes. The rendered SVG still
	// asks the browser for Helvetica/Arial/sans-serif via the CSS in the editor view.
	tmpDot += '  node [shape=box, style="rounded,filled", fontname="Helvetica", fontsize=11, penwidth=1.6, margin="0.16,0.09"];\n';
	tmpDot += '  edge [shape=normal, fontname="Helvetica", fontsize=10, penwidth=1.6, arrowsize=0.85];\n';

	// State nodes: label = wrapped display name; fill = light tint, border + font tuned per state colour.
	tmpStates.forEach((pState, pIndex) =>
	{
		const tmpColor = libModel.stateColor(pState.Status);
		const tmpTint = libModel.stateTint(pState.Status);
		const tmpLabel = _wrap(pState.StateName || pState.Status, 22);
		tmpDot += `  s${pIndex} [id="s${pIndex}", label="${_escape(tmpLabel)}", fillcolor="${tmpTint}", color="${tmpColor}", fontcolor="#2c3e50"];\n`;
	});

	// Transition edges (+ flattened branch edges, dashed). Edge colour = destination state's colour.
	const tmpEdgeMap = {};
	let tmpEdgeIndex = 0;
	tmpStates.forEach((pState) =>
	{
		const tmpSourceId = tmpIdByStatus[pState.Status];
		(pState.Transitions || []).forEach((pTransition, pTransitionIndex) =>
		{
			if (tmpIdByStatus.hasOwnProperty(pTransition.ToStatus))
			{
				const tmpEid = `e${tmpEdgeIndex++}`;
				const tmpColor = libModel.stateColor(pTransition.ToStatus);
				tmpDot += `  ${tmpSourceId} -> ${tmpIdByStatus[pTransition.ToStatus]} [id="${tmpEid}", label="${_escape(pTransition.Action || '')}", color="${tmpColor}", fontcolor="${tmpColor}"];\n`;
				tmpEdgeMap[tmpEid] = { SourceStatus: pState.Status, TransitionIndex: pTransitionIndex, IsBranch: false, ToStatus: pTransition.ToStatus };
			}
			_flattenBranchTargets(pTransition.Branches).forEach((pBranch) =>
			{
				if (tmpIdByStatus.hasOwnProperty(pBranch.ToStatus))
				{
					const tmpEid = `e${tmpEdgeIndex++}`;
					const tmpColor = libModel.stateColor(pBranch.ToStatus);
					tmpDot += `  ${tmpSourceId} -> ${tmpIdByStatus[pBranch.ToStatus]} [id="${tmpEid}", label="${_escape(pTransition.Action || '')}", color="${tmpColor}", fontcolor="${tmpColor}", style="dashed"];\n`;
					tmpEdgeMap[tmpEid] = { SourceStatus: pState.Status, TransitionIndex: pTransitionIndex, IsBranch: true, ToStatus: pBranch.ToStatus };
				}
			});
		});
	});

	tmpDot += '}\n';
	return { Dot: tmpDot, NodeMap: tmpNodeMap, EdgeMap: tmpEdgeMap };
}

module.exports = { blobToDot };
